import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, Lock, LockOpen, CheckCircle2, AlertCircle, FileWarning,
    ChevronDown, ChevronUp, RefreshCw, Shield,
} from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
    SECCIONES_F08, fmtDate, diasRestantes,
} from "./_common";

const ITEM_BADGE = {
    completo: "bg-emerald-50 text-emerald-700 border-emerald-200",
    excepcion: "bg-amber-50 text-amber-700 border-amber-300",
    faltante: "bg-red-50 text-red-700 border-red-200",
};

const RESULTADO_BADGE = {
    completo: "bg-emerald-100 text-emerald-800 border-emerald-300",
    con_excepciones: "bg-amber-100 text-amber-800 border-amber-300",
    incompleto: "bg-red-100 text-red-800 border-red-300",
};

const RESULTADO_LABEL = {
    completo: "COMPLETO",
    con_excepciones: "CON EXCEPCIONES",
    incompleto: "INCOMPLETO",
};

export default function ChecklistF08() {
    const { fideicomiso_id } = useParams();
    const navigate = useNavigate();
    const [list, setList] = useState(null);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState({ A_constitutivo: true });
    const [editItem, setEditItem] = useState(null); // {seccion, index, item}
    const [editEstado, setEditEstado] = useState("completo");
    const [editNotas, setEditNotas] = useState("");
    const [excDialog, setExcDialog] = useState(null); // {seccion, index}
    const [excMotivo, setExcMotivo] = useState("");
    const [excSesion, setExcSesion] = useState("");
    const [excVence, setExcVence] = useState("");
    const [habMotivo, setHabMotivo] = useState("");
    const [habOpen, setHabOpen] = useState(false);
    const [habilitando, setHabilitando] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/aceptacion/checklists", {
                params: { fideicomiso_id, limit: 10 },
            });
            if (data && data.length > 0) {
                setList(data[0]);
            } else {
                setList({ _empty: true });
            }
        } catch (err) {
            toast.error("No se pudo cargar el checklist");
        } finally { setLoading(false); }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fideicomiso_id]);

    const guardarItem = async () => {
        try {
            await api.patch(
                `/aceptacion/checklists/${list.id}/items/${editItem.seccion}/${editItem.index}`,
                {
                    estado: editEstado,
                    notas: editNotas || null,
                },
            );
            toast.success("Item actualizado");
            setEditItem(null);
            setEditNotas("");
            load();
        } catch (err) {
            toast.error("No se pudo actualizar", {
                description: err?.response?.data?.detail,
            });
        }
    };

    const crearExcepcion = async () => {
        if (excMotivo.length < 5) {
            toast.error("Motivo mínimo 5 caracteres"); return;
        }
        if (!excSesion || !excVence) {
            toast.error("Captura sesión de comité y fecha de vencimiento"); return;
        }
        try {
            await api.post(`/aceptacion/checklists/${list.id}/excepciones`, {
                seccion: excDialog.seccion,
                item_index: excDialog.index,
                motivo: excMotivo,
                sesion_comite_id: excSesion,
                vence_at: excVence,
            });
            toast.success("Excepción autorizada");
            setExcDialog(null);
            setExcMotivo(""); setExcSesion(""); setExcVence("");
            load();
        } catch (err) {
            toast.error("No se pudo autorizar", {
                description: err?.response?.data?.detail,
            });
        }
    };

    const habilitar = async () => {
        if (habMotivo.length < 5) {
            toast.error("Motivo mínimo 5 caracteres"); return;
        }
        setHabilitando(true);
        try {
            await api.post(`/aceptacion/fideicomisos/${fideicomiso_id}/habilitar`, {
                motivo: habMotivo, fideicomiso_id,
            });
            toast.success("Fideicomiso habilitado · estado activo");
            setHabOpen(false);
            setHabMotivo("");
            setTimeout(() => navigate(`/fideicomisos/${fideicomiso_id}`), 800);
        } catch (err) {
            toast.error("No se pudo habilitar", {
                description: err?.response?.data?.detail,
            });
        } finally { setHabilitando(false); }
    };

    if (!list) {
        return <div className="p-8">{loading ? "Cargando..." : ""}</div>;
    }

    if (list._empty) {
        return (
            <div className="p-8 lg:p-10 max-w-2xl text-center">
                <AlertCircle className="h-14 w-14 text-amber-500 mx-auto mb-4" />
                <h2 className="font-display text-2xl mb-2">Sin checklist F-08</h2>
                <p className="text-sm text-gray-600 mb-6">
                    Este fideicomiso aún no tiene checklist de expediente. Debes crearlo antes de poder habilitarlo.
                </p>
                <Button onClick={async () => {
                    try {
                        const { data } = await api.post("/aceptacion/checklists", {
                            fideicomiso_id,
                            producto: "P-GAR",
                        });
                        toast.success("Checklist creado");
                        load();
                    } catch (err) {
                        toast.error("No se pudo crear",
                            { description: err?.response?.data?.detail });
                    }
                }}>Crear checklist (producto por defecto P-GAR)</Button>
            </div>
        );
    }

    // Recalcular métricas en vivo
    const items_totales = SECCIONES_F08.reduce((acc, s) =>
        acc + (list.secciones?.[s.key]?.length || 0), 0);
    const items_completo = SECCIONES_F08.reduce((acc, s) => acc +
        (list.secciones?.[s.key] || []).filter((it) => it.estado === "completo").length, 0);
    const items_excepcion = SECCIONES_F08.reduce((acc, s) => acc +
        (list.secciones?.[s.key] || []).filter((it) => it.estado === "excepcion").length, 0);
    const items_faltante = items_totales - items_completo - items_excepcion;

    const excepciones = list.excepciones_autorizadas || list.excepciones || [];
    const excepcionesVencidas = excepciones.filter((e) =>
        e.estado === "activa" && diasRestantes(e.vence_at) <= 0);

    const puedeHabilitar =
        list.resultado_f08 !== "incompleto" && excepcionesVencidas.length === 0;
    const motivoNoHabilitar =
        list.resultado_f08 === "incompleto"
            ? `Faltan ${items_faltante} item(s) obligatorio(s) por completar.`
            : excepcionesVencidas.length > 0
                ? `Hay ${excepcionesVencidas.length} excepción(es) vencida(s) sin cerrar.`
                : "";

    return (
        <div className="p-8 lg:p-10 max-w-[1600px] space-y-6" data-testid="checklist-f08-page">
            <div className="flex items-center gap-3">
                <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
                    <ArrowLeft className="h-4 w-4 mr-1" /> Volver
                </Button>
            </div>

            <div className="flex items-start justify-between gap-6 flex-wrap">
                <div>
                    <div className="font-mono text-xs text-gray-500 mb-1">
                        {list.folio} · Fideicomiso {fideicomiso_id}
                    </div>
                    <h1 className="font-display text-3xl text-[color:var(--brand-charcoal)]">
                        Checklist F-08 · Candado de habilitación
                    </h1>
                    <p className="text-sm text-[color:var(--brand-charcoal)]/70 mt-1">
                        Producto {list.producto} · {items_completo}/{items_totales} items en orden.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={load}>
                        <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
                    </Button>
                    <Button
                        onClick={() => setHabOpen(true)}
                        disabled={!puedeHabilitar}
                        title={!puedeHabilitar ? motivoNoHabilitar : "Habilitar fideicomiso"}
                        className={puedeHabilitar ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                        data-testid="btn-habilitar-fid"
                    >
                        {puedeHabilitar
                            ? <><LockOpen className="h-4 w-4 mr-2" /> Habilitar fideicomiso</>
                            : <><Lock className="h-4 w-4 mr-2" /> Habilitar fideicomiso</>}
                    </Button>
                </div>
            </div>

            {!puedeHabilitar && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 flex items-start gap-3">
                    <Lock className="h-5 w-5 shrink-0 mt-0.5" />
                    <div>
                        <strong>Candado activo:</strong> {motivoNoHabilitar}
                    </div>
                </div>
            )}

            <div className="grid lg:grid-cols-[1fr_320px] gap-6">
                {/* Secciones accordion */}
                <div className="space-y-3">
                    {SECCIONES_F08.map((s) => {
                        const items = list.secciones?.[s.key] || [];
                        const isOpen = expanded[s.key];
                        const total = items.length;
                        const done = items.filter((i) => i.estado === "completo").length;
                        return (
                            <section key={s.key}
                                className="rounded-lg border border-gray-200 bg-white overflow-hidden"
                                data-testid={`seccion-${s.key}`}>
                                <button
                                    onClick={() => setExpanded((p) => ({ ...p, [s.key]: !isOpen }))}
                                    className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
                                >
                                    <div className="flex items-center gap-3">
                                        <h3 className="font-display text-base">{s.label}</h3>
                                        <span className="text-xs text-gray-500">{done}/{total}</span>
                                    </div>
                                    {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                </button>
                                {isOpen && (
                                    <div className="border-t divide-y">
                                        {items.length === 0 && (
                                            <div className="p-5 text-sm text-gray-400 italic">
                                                Sin items configurados para esta sección.
                                            </div>
                                        )}
                                        {items.map((it, idx) => (
                                            <div key={idx} className="p-4 flex items-start gap-3 hover:bg-gray-50"
                                                data-testid={`item-${s.key}-${idx}`}>
                                                <div className="pt-0.5">
                                                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${ITEM_BADGE[it.estado] || ITEM_BADGE.faltante}`}>
                                                        {it.estado}
                                                    </span>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-medium">
                                                        {it.nombre}
                                                        {it.obligatorio && <span className="text-red-500 ml-1">*</span>}
                                                    </div>
                                                    {it.notas && (
                                                        <div className="text-xs text-gray-600 italic mt-1">
                                                            {it.notas}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex gap-1">
                                                    <Button size="sm" variant="ghost" onClick={() => {
                                                        setEditItem({ seccion: s.key, index: idx, item: it });
                                                        setEditEstado("completo");
                                                        setEditNotas(it.notas || "");
                                                    }}>Editar</Button>
                                                    {it.estado !== "excepcion" && (
                                                        <Button size="sm" variant="ghost"
                                                            onClick={() => setExcDialog({ seccion: s.key, index: idx })}>
                                                            <FileWarning className="h-3.5 w-3.5" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </section>
                        );
                    })}
                </div>

                {/* Panel lateral permanente */}
                <aside className="space-y-4">
                    <div className="rounded-lg border border-gray-200 bg-white p-5 sticky top-6">
                        <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">
                            Resultado F-08
                        </div>
                        <div className={`inline-block text-sm font-bold tracking-wider px-3 py-1 rounded border ${RESULTADO_BADGE[list.resultado_f08] || "bg-gray-100"}`}
                            data-testid="resultado-f08">
                            {RESULTADO_LABEL[list.resultado_f08] || list.resultado_f08}
                        </div>

                        <div className="mt-5 space-y-2 text-sm">
                            <Stat label="Completos" value={items_completo} color="text-emerald-700" />
                            <Stat label="Excepciones" value={items_excepcion} color="text-amber-700" />
                            <Stat label="Faltantes" value={items_faltante} color="text-red-700" />
                        </div>

                        {excepciones.length > 0 && (
                            <div className="mt-5 pt-5 border-t">
                                <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">
                                    Excepciones activas
                                </div>
                                <ul className="space-y-2">
                                    {excepciones.filter((e) => e.estado === "activa").map((e, i) => {
                                        const d = diasRestantes(e.vence_at);
                                        const color = d <= 0 ? "text-red-700 font-semibold" :
                                            d <= 7 ? "text-amber-700" : "text-gray-600";
                                        return (
                                            <li key={i} className="text-xs rounded border border-amber-200 bg-amber-50 p-2">
                                                <div className="font-medium">{e.seccion} · item {e.item_index}</div>
                                                <div className={`${color} mt-1`}>
                                                    Vence {fmtDate(e.vence_at)}
                                                    {d !== null && ` (${d}d)`}
                                                </div>
                                                <div className="italic text-gray-600 mt-1">{e.motivo}</div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}

                        <div className="mt-5 pt-5 border-t text-[11px] text-gray-500 flex items-center gap-2">
                            <Shield className="h-3 w-3" />
                            Al habilitar se registra en bitácora inmutable.
                        </div>
                    </div>
                </aside>
            </div>

            {/* Dialog editar item */}
            <Dialog open={!!editItem} onOpenChange={() => setEditItem(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Editar item · {editItem?.item?.nombre}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <label className="text-xs uppercase tracking-wider text-gray-600">Estado</label>
                            <div className="flex gap-2 mt-1">
                                {["completo", "faltante"].map((e) => (
                                    <button key={e} type="button"
                                        onClick={() => setEditEstado(e)}
                                        className={`px-3 py-1.5 rounded border text-sm transition ${editEstado === e ? "bg-[color:var(--brand-charcoal)] text-white border-[color:var(--brand-charcoal)]" : "border-gray-300 hover:border-gray-400"}`}>
                                        {e}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-wider text-gray-600">Notas</label>
                            <textarea value={editNotas} onChange={(e) => setEditNotas(e.target.value)}
                                className="w-full min-h-[80px] rounded-md border p-2 text-sm" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditItem(null)}>Cancelar</Button>
                        <Button onClick={guardarItem}>Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog excepción */}
            <Dialog open={!!excDialog} onOpenChange={() => setExcDialog(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Autorizar excepción</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <label className="text-xs uppercase tracking-wider text-gray-600">Motivo *</label>
                            <textarea value={excMotivo} onChange={(e) => setExcMotivo(e.target.value)}
                                className="w-full min-h-[80px] rounded-md border p-2 text-sm" minLength={5} maxLength={1000} />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-wider text-gray-600">
                                ID sesión Comité que autoriza *
                            </label>
                            <Input value={excSesion} onChange={(e) => setExcSesion(e.target.value)}
                                placeholder="sesion_abcd1234" />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-wider text-gray-600">Vence el *</label>
                            <Input type="date" value={excVence} onChange={(e) => setExcVence(e.target.value)} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setExcDialog(null)}>Cancelar</Button>
                        <Button onClick={crearExcepcion}>Autorizar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog habilitar */}
            <Dialog open={habOpen} onOpenChange={setHabOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Habilitar fideicomiso</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <p className="text-sm text-gray-600">
                            Al habilitar, el fideicomiso pasa a estado <strong>activo</strong> y se registra en bitácora inmutable
                            con tu identidad y timestamp. Esta acción no se puede deshacer.
                        </p>
                        <div>
                            <label className="text-xs uppercase tracking-wider text-gray-600">Motivo *</label>
                            <textarea value={habMotivo} onChange={(e) => setHabMotivo(e.target.value)}
                                className="w-full min-h-[90px] rounded-md border p-2 text-sm" minLength={5}
                                data-testid="habilitar-motivo" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setHabOpen(false)}>Cancelar</Button>
                        <Button onClick={habilitar} disabled={habilitando}
                            className="bg-emerald-600 hover:bg-emerald-700"
                            data-testid="habilitar-submit">
                            {habilitando ? "Habilitando…" : "Habilitar ahora"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Stat({ label, value, color }) {
    return (
        <div className="flex justify-between items-center">
            <span className="text-xs text-gray-500">{label}</span>
            <span className={`font-bold text-base ${color}`}>{value}</span>
        </div>
    );
}
